<template>
  <div class="w-full max-h-full h-full px-6 py-4">
    <o-table
      v-if="_employee"
      class="w-full h-full"
      container-class="border rounded bg-white dark:bg-gray-900 dark:border-gray-800 overflow-hidden"
      :data="_employee.services"
      :headers="[
        {text: $t('general.enabled')},
        {text: $t('general.service')},
        {text: $t('general.price')},
        {text: $t('general.duration')},
        {text: $t('general.processing')},
        {text: $t('general.finishing')},
        {text: $t('general.online')},
        {text: $t('general.availableDays')}
      ]"
      :limit="20"
      :filter-keys="[
        'service.title',
        'price'
      ]"
      :thead-class="{
        thead: 'border-b dark:border-gray-900',
        tr: 'border-blue-700',
        th: 'font-normal p-2 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-white text-base sticky top-0 text-left',
        content: 'flex item-center'
      }"
      :tbody-class="{
        tfoot: '',
        tr: 'transition-all duration-200 bg-gray-50 dark:bg-gray-900',
        td: 'py-3 px-2 text-base',
        stripe: 'transition-all duration-200 bg-gray-50/50 dark:bg-white/[0.01]'
      }"
      striped
      filter
      collapse
      scroll
    >
      <template #thead="{ tbodyClass, thClass, trClass }">
        <thead :class="tbodyClass">
          <tr :class="trClass">
            <th :class="thClass">
              <div class="flex items-center space-x-2">
                <o-tooltip :text="$t('components.employee.edit.services.enableServices')">
                  <checkbox-field v-model="_enabled" class="mt-1" />
                </o-tooltip>

                <p class="leading-none">
                  {{ $t('general.enabled') }}
                </p>
              </div>
            </th>
            <th :class="thClass">
              <p class="leading-none">
                {{ $t('general.service') }}
              </p>
            </th>
            <th :class="thClass">
              <p class="leading-none">
                {{ $t('general.price') }}
              </p>
            </th>
            <th :class="thClass">
              <p class="leading-none">
                {{ $t('general.duration') }}
              </p>
            </th>
            <th :class="thClass">
              <p class="leading-none">
                {{ $t('general.processing') }}
              </p>
            </th>
            <th :class="thClass">
              <p class="leading-none">
                {{ $t('general.finishing') }}
              </p>
            </th>
            <th :class="thClass">
              <div class="flex items-center space-x-2">
                <o-tooltip :text="$t('components.employee.edit.services.enableOnlineBooking')">
                  <checkbox-field v-model="_online" class="mt-1" />
                </o-tooltip>

                <p class="leading-none">
                  {{ $t('general.online') }}
                </p>
              </div>
            </th>
            <th :class="thClass">
              <p class="leading-none">
                {{ $t('general.availableDays') }}
              </p>
            </th>
          </tr>
        </thead>
      </template>

      <template #row="props">
        <td :class="props.tdClass">
          <toggle-field v-model="props.row.enabled" @change="handleToggleEnable(props.row)" />
        </td>
        <td :class="props.tdClass" class="w-48 line-clamp-3">
          <span :class="{ 'opacity-50' : !props.row.enabled }">{{ props.row.service.title }}</span>
        </td>
        <td :class="props.tdClass" class="min-w-32">
          <currency-field v-model="props.row.price" :disabled="!props.row.enabled" :placeholder="props.row.service.price" :class="{ 'ring-2 ring-yellow-300 ring-offset-1' : !!props.row.price }" @change="handleServicePriceChange" />
        </td>
        <td :class="props.tdClass" class="min-w-24">
          <number-field
            v-model="props.row.duration"
            slim
            :min="0"
            :disabled="!props.row.enabled"
            :placeholder="props.row.service.duration"
            :class="{ 'ring-2 ring-yellow-300 ring-offset-1' : !!props.row.duration }"
          />
        </td>
        <td :class="props.tdClass" class="min-w-24">
          <number-field v-model="props.row.processing" slim :disabled="!props.row.enabled" :placeholder="props.row.service.processing" :class="{ 'ring-2 ring-yellow-300 ring-offset-1' : !!props.row.processing }" />
        </td>
        <td :class="props.tdClass" class="min-w-24">
          <number-field v-model="props.row.finishing" slim :disabled="!props.row.enabled" :placeholder="props.row.service.finishing" :class="{ 'ring-2 ring-yellow-300 ring-offset-1' : !!props.row.finishing }" />
        </td>
        <td :class="props.tdClass">
          <allow-internet
            :value="props.row.allowInternet"
            :default="_employee.allowInternet"
            :enabled="props.row.enabled"
            @change="(val) => props.row.allowInternet = val"
          />
        </td>
        <td :class="props.tdClass" class="w-32">
          <div clas="flex items-center">
            <p class="inline mr-2 text-sm">
              {{ props.row.enabled && props.row.allowedDays ? $t(props.row.availableDaysLabel) : $t('general.none') }}
            </p>
            <o-button v-if="props.row.enabled" flat size="xs" icon="edit" @click="props.toggle" />
          </div>
        </td>
      </template>

      <template #collapse="{ row }">
        <div>
          <div class="mb-3">
            <h4 class="text-sm">
              {{ $t('general.availableDays') }}
            </h4>
            <p class="text-sm opacity-80">
              {{ $t('employee.limitService') }}
            </p>
          </div>

          <div class="flex items-center">
            <template v-if="row.allowedDays">
              <div
                v-for="(day, key) in row.allowedDays"
                :key="key"
                class="pb-2 pr-2"
              >
                <checkbox-field
                  v-slot="{ isChecked }"
                  v-model="row.allowedDays[key]"
                  :label="key"
                  base-class="hidden"
                  active-class=""
                >
                  <div
                    :class="[
                      'px-2 py-1 text-sm rounded capitalize flex items-center cursor-pointer transform hover:scale-104 transition-transform',
                      isChecked ? 'bg-green-400 dark:bg-emerald-900 text-white' : 'bg-gray-200 dark:bg-gray-700 opacity-80'
                    ]"
                  >
                    <o-icon :icon="isChecked ? 'check' : 'cross'" fill class="mr-2" :size="12" />
                    <span>{{ key }}</span>
                  </div>
                </checkbox-field>
              </div>
            </template>
          </div>
        </div>
      </template>
    </o-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

import AllowInternet from '@/components/employee/elements/AllowInternet.vue'

import {
  OTable,
} from '@/components/elements'

import {
  ToggleField,
  CurrencyField,
  NumberField,
  CheckboxField,
} from '@/components/fields'

interface Props {
  employee: Record<string, any>
  handleServicePriceChange: Function
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:employee', value: Record<string, any>): void
}>()

const enabled = ref(false)
const online = ref(false)

const _employee = computed({
  get () {
    return props.employee
  },
  set (value) {
    emit('update:employee', value)
  },
})

const _enabled = computed({
  get () {
    return props.employee.services
      .every((service: any) => service.enabled)
  },
  set (value: boolean) {
    for (const service of props.employee.services) {
      service.enabled = value
    }
  },
})

const _online = computed({
  get () {
    return props.employee.services
      .every((service: any) => service.allowInternet)
  },
  set (newValue: boolean) {
    for (const service of props.employee.services) {
      service.allowInternet = newValue
    }
  },
})

function handleToggleEnable (employeeService: any) {
  if (employeeService.enabled && !employeeService.allowedDays) {
    const allowedDays = { monday:true, tuesday:true, wednesday:true, thursday:true, friday:true, saturday:true, sunday:true }
    employeeService.allowedDays = allowedDays
  }
}
</script>
